import { FunctionComponent, ReactNode, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import {
    encodeUnicode,
    safeSetTimeout,
    useMediaQuery,
    WebcastHelper,
    WebcastStatus,
} from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { useButtonAnalytics, useEntryUrl } from "@kaltura/mediaspace-shared-hooks";
import { EventListItemModal } from "../event-list-item-modal/EventListItemModal";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    EventListItemContentContainer,
    StyledActionSection,
    EventListItemRowContainer,
    EventListItemContainer,
} from "./styled-components";
import { getCallToAction } from "./getCallToAction";
import { RsvpService, RsvpStatus } from "@kaltura/mediaspace-shared-entry";
import { EventListItemContent } from "./EventListItemContent";
import { EventListItemProps } from "./EventListItemProps";
import { EventListItemScheduling } from "./EventListItemScheduling";
import { EventListItemThumbnail } from "./EventListItemThumbnail";

/**
 * Event Session Item, mostly as part of a list of event sessions
 * Displays scheduled-entry data, including scheduling info.
 */
export const EventListItem: FunctionComponent<EventListItemProps> = ({ className = "", ...props }) => {
    const {
        data,
        vodCallback,
        liveCallback,
        inWatchlist,
        disabledMode,
        analyticsValue,
        onToggleWatchList,
        categoryId,
        calendarEntriesToken,
    } = props;
    let { entryLink } = props;

    const context = useConfig();

    // context params
    const joinBuffer = context.eventPlatform.eventListItem.joinBuffer;

    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

    const sendButtonAnalytics = useButtonAnalytics();

    const [callToActionButton, setCallToActionButton] = useState<ReactNode | null>(null);
    const [rsvpStatus, setRsvpStatus] = useState<RsvpStatus | undefined>();

    const onRsvpChange = useCallback(
        (status: RsvpStatus | undefined) => {
            setRsvpStatus(status);
        },
        []
    );

    const handleGetCallToAction = useCallback(
        (bufferStatus: WebcastStatus) => {
            setCallToActionButton(
                getCallToAction(
                    bufferStatus,
                    data.id,
                    data,
                    vodCallback,
                    liveCallback,
                    context,
                    disabledMode,
                    categoryId,
                    rsvpStatus,
                    onRsvpChange,
                    calendarEntriesToken
                )
            );
        },
        [
            categoryId,
            context,
            data,
            disabledMode,
            liveCallback,
            onRsvpChange,
            vodCallback,
            rsvpStatus,
            calendarEntriesToken,
        ]
    );

    // UseEffect to fetch initial RSVP status for future entries if cross-site RSVP enabled
    // runs only when component mounted
    useEffect(() => {
        const bufferSchedulingData = {
            start: { ...data.schedulingData.start },
            end: { ...data.schedulingData.end },
        };
        const bufferStatus = WebcastHelper.getLiveStatus(bufferSchedulingData);
        if (bufferStatus === WebcastStatus.future && context.rsvp?.isCrossSite) {
            if (context.application.isLoggedIn) {
                RsvpService.getRsvpStatus(data.id).then((rsvpStatusResult) => {
                    setRsvpStatus(rsvpStatusResult);
                });
            }
            else {
                setRsvpStatus(RsvpStatus.UNCONFIRMED);
            }
        }
    }, []);

    /**
     * update CTA from 'Add to Calendar' or 'RSVP' to 'Join now' when the joinBuffer is reached.
     * see SessionCardJoinNowIndication config in Widgets module for more info.
     * default is 15 minutes before event start time
     */
    useEffect(() => {
        // decrease joinBuffer from start timestamp
        const bufferSchedulingData = WebcastHelper.applyJoinBuffer(data.schedulingData, joinBuffer);
        // get live status per join buffer
        const bufferStatus = WebcastHelper.getLiveStatus(bufferSchedulingData);
        // get call to action per buffer live status
        handleGetCallToAction(bufferStatus);

        // calculate seconds to joinBuffer
        const now = Date.now() / 1000;
        const secondsUntilBuffer = bufferSchedulingData.start.timestamp - now;

        // return if event is not in the future
        if (secondsUntilBuffer < 0) {
            return;
        }

        // schedule updating CTA from 'Add to Calendar' to 'Join now' when joinBuffer is reached
        const timeoutId = safeSetTimeout(
            () => {
                handleGetCallToAction(WebcastStatus.live);
            },
            secondsUntilBuffer * 1000
        );

        return () => clearTimeout(timeoutId);
    }, [data.schedulingData, handleGetCallToAction, joinBuffer]);

    const [showReadMoreModal, setShowReadMoreModal] = useState(false);

    entryLink = useEntryUrl({ entry: data, entryUrl: entryLink });

    const handleShowMore = (action: string) => {
        setShowReadMoreModal(true);
        sendButtonAnalytics(action, ButtonClickAnalyticsType.LAUNCH);
    };

    return (
        <EventListItemContainer
            className={clsx("kms-ds-event-list-item", className)}
            data-analytics={encodeUnicode(data.name)}
        >
            <EventListItemRowContainer>
                <EventListItemScheduling schedulingData={data.schedulingData} vertical={isLargeScreen} />

                <EventListItemContentContainer className="kms-ds-event-list-item-content">
                    <EventListItemContent
                        {...props}
                        onPresentersShowMore={() => handleShowMore("Session card - Speakers more button")}
                    />
                </EventListItemContentContainer>

                <EventListItemModal
                    data={data}
                    show={showReadMoreModal}
                    onHide={() => setShowReadMoreModal(false)}
                    callToActionButton={callToActionButton}
                    inWatchlist={inWatchlist}
                    onToggleWatchList={onToggleWatchList}
                />

                {isLargeScreen && (
                    <EventListItemThumbnail
                        entry={data}
                        entryLink={entryLink}
                        disabled={disabledMode}
                        analyticsValue={analyticsValue}
                    />
                )}
            </EventListItemRowContainer>

            <StyledActionSection
                className="kms-ds-event-list-item-actions"
                data={data}
                callToActionButton={callToActionButton}
                onShowMore={() => handleShowMore("Session card - more info button")}
                inWatchlist={inWatchlist}
                onToggleWatchList={onToggleWatchList}
            />
        </EventListItemContainer>
    );
};
